import { useEffect, useState } from 'react'
import useAsync from 'react-use/esm/useAsync'
import FileUpload from '~/components/common/FileUpload'
import { SimpleTextarea } from '~/components/common/SimpleTextarea'
import { AgentRequest } from '~/protos/agents'
import { getFileBase64 } from '~/utils/shared'
import { TaskParams } from '../../BaseTaskForm'

interface MappingTableToDWLFormProps {
  onChange: (task: TaskParams) => void
  withExamplePrefilled?: boolean
  initialTaskData?: TaskParams | null
}

export function MappingTableToDWLForm({
  onChange,
  withExamplePrefilled,
  initialTaskData
}: MappingTableToDWLFormProps) {
  // Initialize from initialTaskData if editing
  const initialUserNotes = initialTaskData?.request?.userNotes ?? ''

  const [mappingTable, setMappingTable] = useState<File | null>(null)
  const [userNotes, setUserNotes] = useState<string>(initialUserNotes)
  const [hasExistingMapping, setHasExistingMapping] = useState(false)

  // Check if there's existing mapping data from initialTaskData
  useEffect(() => {
    if (initialTaskData?.request?.mappingSheet?.length) {
      setHasExistingMapping(true)
    }
    if (initialTaskData?.request?.userNotes) {
      setUserNotes(initialTaskData.request.userNotes)
    }
  }, [initialTaskData])

  useAsync(async () => {
    // If we have a new mapping table upload, use it
    if (mappingTable) {
      let text = 'Mapping Table Uploaded'
      if (userNotes) text += `### User Notes\n\`\`\`json\n${userNotes}\n\`\`\``

      const request = AgentRequest.fromPartial({
        command: { commandString: text },
        mappingSheet: [
          {
            id: mappingTable.name || 'mapping_table.xlsx',
            base64EncodedXlsx: await getFileBase64(mappingTable)
          }
        ],
        userNotes
      })
      onChange?.({ request })
    }
    // If editing and no new upload, preserve existing mapping data
    else if (hasExistingMapping && initialTaskData?.request?.mappingSheet) {
      let text = 'Mapping Table Uploaded'
      if (userNotes) text += `### User Notes\n\`\`\`json\n${userNotes}\n\`\`\``

      const request = AgentRequest.fromPartial({
        command: { commandString: text },
        mappingSheet: initialTaskData.request.mappingSheet,
        userNotes
      })
      onChange?.({ request })
    }
  }, [mappingTable, userNotes, hasExistingMapping, initialTaskData])

  return (
    <>
      <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[15px] select-none'>
        Mapping Table*:
      </div>
      {hasExistingMapping && !mappingTable && (
        <div className='text-sm text-gray-600 mb-2 p-2 bg-gray-50 rounded border border-gray-200 select-none'>
          Using existing mapping table from previous task
        </div>
      )}
      <FileUpload
        onFileChange={setMappingTable}
        withExamplePrefilled={withExamplePrefilled}
        disabled={withExamplePrefilled}
      />
      <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] select-none'>
        Notes (Optional):
      </div>
      <SimpleTextarea
        onChange={setUserNotes}
        minRows={5}
        placeholder='Additional notes about the source data and transformations to help understand your needs better.'
        defaultValue={initialUserNotes}
        disabled={withExamplePrefilled}
      />
    </>
  )
}
